import { AIModule } from '../types';

export const AI_ETHICS_FUNDAMENTALS_MODULES: AIModule[] = [
  {
    id: 'ethics-intro',
    title: 'Introduction to AI Ethics',
    description: 'Understanding ethical challenges in AI development and deployment.',
    duration: '2.5 hours',
    difficulty: 'beginner',
    content: `
# Introduction to AI Ethics

## What is AI Ethics?

AI Ethics is the branch of ethics that examines the moral implications of artificial intelligence systems. It addresses how AI should be developed, deployed, and used to ensure beneficial outcomes for humanity.

## Why AI Ethics Matters

### 1. Scale and Impact
- **Widespread deployment**: AI affects billions of people daily
- **High-stakes decisions**: Healthcare, criminal justice, financial services
- **Automation of bias**: AI can perpetuate and amplify human biases
- **Irreversible consequences**: Some AI decisions cannot be undone

### 2. Unique Challenges
- **Black box problem**: Difficulty understanding AI decision-making
- **Emergent behaviors**: AI systems may behave in unexpected ways
- **Speed of deployment**: Technology evolves faster than regulations
- **Cross-border implications**: Global impact of AI systems

## Core Ethical Principles

### 1. Fairness and Non-Discrimination
**Definition**: AI systems should treat all individuals and groups equitably.

**Key Considerations**:
- **Algorithmic bias**: Unfair treatment based on protected characteristics
- **Equal opportunity**: Ensuring fair access to AI benefits
- **Representation**: Including diverse perspectives in AI development
- **Historical bias**: Addressing biases present in training data

**Examples**:
- Hiring algorithms that discriminate against women
- Facial recognition systems with higher error rates for dark-skinned individuals
- Credit scoring systems that disadvantage certain ethnic groups

### 2. Transparency and Explainability
**Definition**: AI systems should be understandable and their decisions explainable.

**Components**:
- **Algorithmic transparency**: Understanding how AI systems work
- **Decision explanations**: Providing reasons for specific outcomes
- **Data transparency**: Clarity about data sources and processing
- **Process transparency**: Open development and deployment processes

**Challenges**:
- Trade-offs between accuracy and interpretability
- Protecting proprietary algorithms
- Technical complexity of explanations
- Different explanation needs for different stakeholders

### 3. Privacy and Data Protection
**Definition**: AI systems should respect individual privacy rights and protect personal data.

**Key Areas**:
- **Data minimization**: Collecting only necessary data
- **Purpose limitation**: Using data only for stated purposes
- **Consent management**: Obtaining proper user consent
- **Anonymization**: Protecting individual identities in datasets

**Emerging Concerns**:
- Facial recognition and surveillance
- Behavioral profiling and manipulation
- Re-identification of anonymous data
- Cross-border data transfers

### 4. Accountability and Responsibility
**Definition**: Clear assignment of responsibility for AI system outcomes.

**Stakeholder Responsibilities**:
- **Developers**: Responsible for technical implementation
- **Deployers**: Responsible for appropriate use and monitoring
- **Users**: Responsible for proper interaction with AI systems
- **Regulators**: Responsible for oversight and enforcement

**Challenges**:
- Complex supply chains in AI development
- Autonomous system decision-making
- Distributed responsibility across multiple parties
- Legal frameworks lagging behind technology

### 5. Human Autonomy and Agency
**Definition**: AI should augment rather than replace human decision-making capacity.

**Principles**:
- **Human oversight**: Maintaining meaningful human control
- **Informed consent**: Users understanding AI involvement
- **Right to explanation**: Understanding AI-influenced decisions
- **Opt-out options**: Ability to avoid AI-driven processes

## Ethical Frameworks

### 1. Consequentialist Approaches
Focus on outcomes and consequences of AI deployment.

**Utilitarianism**: Maximize overall well-being and minimize harm
- Benefits: Clear metric for evaluation
- Challenges: Difficult to measure utility, potential for minority oppression

### 2. Deontological Approaches
Focus on duties and rules regardless of consequences.

**Rights-based ethics**: Respect fundamental human rights
- Benefits: Clear moral boundaries
- Challenges: Conflicts between different rights

### 3. Virtue Ethics
Focus on character traits and virtues of AI developers and users.

**Key virtues**: Honesty, justice, compassion, wisdom
- Benefits: Emphasis on moral character
- Challenges: Cultural variation in virtue definitions

### 4. Care Ethics
Focus on relationships, care, and contextual moral reasoning.

**Principles**: Attentiveness, responsibility, competence, responsiveness
- Benefits: Consideration of vulnerable populations
- Challenges: Difficulty scaling to large systems

## Stakeholder Perspectives

### 1. Individual Users
**Concerns**:
- Privacy and data protection
- Fair treatment and non-discrimination
- Understanding AI decisions affecting them
- Control over AI interaction

### 2. Organizations
**Concerns**:
- Compliance with regulations
- Reputation and public trust
- Liability and risk management
- Competitive advantage vs ethical considerations

### 3. Society
**Concerns**:
- Economic inequality and job displacement
- Democratic processes and information integrity
- Social cohesion and human relationships
- Long-term societal development

### 4. Developers and Researchers
**Concerns**:
- Technical feasibility of ethical requirements
- Innovation vs constraint tensions
- Professional responsibility and codes of conduct
- Interdisciplinary collaboration needs

## Common Ethical Dilemmas

### 1. Accuracy vs Fairness
High-performing models may perpetuate biases present in training data.

**Example**: A medical diagnosis AI that's highly accurate but performs worse for underrepresented groups.

### 2. Individual Privacy vs Collective Benefit
Personal data can improve AI systems for everyone but at individual privacy costs.

**Example**: Contact tracing apps during COVID-19 pandemic.

### 3. Transparency vs Security
Explaining AI decisions may reveal vulnerabilities to malicious actors.

**Example**: Anti-fraud systems that could be gamed if fully transparent.

### 4. Autonomy vs Paternalism
Protecting users from harm vs respecting their freedom to make choices.

**Example**: AI content filtering that may limit access to information.

## Emerging Ethical Challenges

### 1. Artificial General Intelligence (AGI)
- Alignment with human values
- Control and containment
- Existential risk considerations
- Transition management

### 2. Autonomous Weapons
- Lethal autonomous weapon systems (LAWS)
- Human control requirements
- International humanitarian law
- Arms race concerns

### 3. Deepfakes and Synthetic Media
- Truth and information integrity
- Consent for likeness use
- Political manipulation
- Evidence authenticity

### 4. AI Governance
- Global coordination needs
- Regulatory approaches
- Technical standard setting
- Democratic participation in AI governance
`,
    objectives: [
      'Understand core ethical principles in AI development',
      'Identify common ethical dilemmas in AI systems',
      'Recognize different stakeholder perspectives on AI ethics',
      'Apply ethical frameworks to AI use cases'
    ],
    codeExamples: [
      {
        id: 'bias-detection',
        title: 'Bias Detection in AI Models',
        description: 'Implementing fairness metrics to detect bias in machine learning models',
        code: `
import numpy as np
import pandas as pd
from sklearn.model_selection import train_test_split
from sklearn.ensemble import RandomForestClassifier
from sklearn.linear_model import LogisticRegression
from sklearn.metrics import accuracy_score, confusion_matrix, classification_report
import matplotlib.pyplot as plt
import seaborn as sns

class FairnessAnalyzer:
    def __init__(self):
        self.model = None
        self.X_test = None
        self.y_test = None
        self.sensitive_features = None
        self.predictions = None
        
    def load_data(self, X, y, sensitive_features, test_size=0.2, random_state=42):
        """Load and split data for fairness analysis"""
        X_train, X_test, y_train, y_test = train_test_split(
            X, y, test_size=test_size, random_state=random_state, stratify=y
        )
        
        # Split sensitive features correspondingly
        sensitive_train, sensitive_test = train_test_split(
            sensitive_features, test_size=test_size, random_state=random_state, stratify=y
        )
        
        self.X_test = X_test
        self.y_test = y_test
        self.sensitive_features = sensitive_test
        
        return X_train, y_train
    
    def train_model(self, X_train, y_train, model_type='random_forest'):
        """Train a model for fairness analysis"""
        if model_type == 'random_forest':
            self.model = RandomForestClassifier(n_estimators=100, random_state=42)
        elif model_type == 'logistic_regression':
            self.model = LogisticRegression(random_state=42, max_iter=1000)
        else:
            raise ValueError("Unsupported model type")
        
        self.model.fit(X_train, y_train)
        self.predictions = self.model.predict(self.X_test)
        
        print(f"Model trained. Test accuracy: {accuracy_score(self.y_test, self.predictions):.3f}")
        
    def demographic_parity(self, sensitive_attribute):
        """Calculate demographic parity (statistical parity)"""
        # P(Y_hat = 1 | A = 0) vs P(Y_hat = 1 | A = 1)
        
        groups = np.unique(self.sensitive_features[sensitive_attribute])
        positive_rates = {}
        
        for group in groups:
            group_mask = self.sensitive_features[sensitive_attribute] == group
            group_predictions = self.predictions[group_mask]
            positive_rate = np.mean(group_predictions == 1)
            positive_rates[group] = positive_rate
        
        # Calculate demographic parity difference
        rates = list(positive_rates.values())
        dp_difference = max(rates) - min(rates)
        
        return positive_rates, dp_difference
    
    def equalized_odds(self, sensitive_attribute):
        """Calculate equalized odds (equality of opportunity)"""
        # TPR and FPR should be equal across groups
        
        groups = np.unique(self.sensitive_features[sensitive_attribute])
        metrics = {}
        
        for group in groups:
            group_mask = self.sensitive_features[sensitive_attribute] == group
            group_y_true = self.y_test[group_mask]
            group_y_pred = self.predictions[group_mask]
            
            # Calculate TPR and FPR
            tn, fp, fn, tp = confusion_matrix(group_y_true, group_y_pred).ravel()
            
            tpr = tp / (tp + fn) if (tp + fn) > 0 else 0  # True Positive Rate
            fpr = fp / (fp + tn) if (fp + tn) > 0 else 0  # False Positive Rate
            
            metrics[group] = {'TPR': tpr, 'FPR': fpr}
        
        # Calculate differences
        tprs = [metrics[group]['TPR'] for group in groups]
        fprs = [metrics[group]['FPR'] for group in groups]
        
        tpr_difference = max(tprs) - min(tprs)
        fpr_difference = max(fprs) - min(fprs)
        
        return metrics, tpr_difference, fpr_difference
    
    def individual_fairness_analysis(self, distance_metric='euclidean'):
        """Analyze individual fairness - similar individuals should receive similar outcomes"""
        from scipy.spatial.distance import pdist, squareform
        
        # Calculate pairwise distances
        distances = squareform(pdist(self.X_test, metric=distance_metric))
        
        # Get prediction probabilities
        if hasattr(self.model, 'predict_proba'):
            pred_probs = self.model.predict_proba(self.X_test)[:, 1]
        else:
            pred_probs = self.predictions.astype(float)
        
        # Calculate outcome differences
        outcome_differences = np.abs(pred_probs[:, np.newaxis] - pred_probs)
        
        # For each point, find k nearest neighbors and check outcome consistency
        k = 5
        consistency_scores = []
        
        for i in range(len(distances)):
            # Get k nearest neighbors (excluding self)
            neighbor_indices = np.argsort(distances[i])[1:k+1]
            neighbor_distances = distances[i][neighbor_indices]
            neighbor_outcome_diffs = outcome_differences[i][neighbor_indices]
            
            # Calculate consistency score (inverse relationship between distance and outcome difference)
            # Lower score means better individual fairness
            if np.mean(neighbor_distances) > 0:
                consistency_score = np.mean(neighbor_outcome_diffs) / np.mean(neighbor_distances)
            else:
                consistency_score = 0
            consistency_scores.append(consistency_score)
        
        return np.array(consistency_scores)
    
    def generate_fairness_report(self, sensitive_attribute):
        """Generate comprehensive fairness report"""
        print("="*60)
        print("FAIRNESS ANALYSIS REPORT")
        print("="*60)
        
        # Overall model performance
        accuracy = accuracy_score(self.y_test, self.predictions)
        print(f"\\nOverall Model Accuracy: {accuracy:.3f}")
        
        # Demographic Parity
        print(f"\\n1. DEMOGRAPHIC PARITY ANALYSIS")
        print("-" * 40)
        positive_rates, dp_diff = self.demographic_parity(sensitive_attribute)
        
        for group, rate in positive_rates.items():
            print(f"Positive prediction rate for {sensitive_attribute}={group}: {rate:.3f}")
        
        print(f"Demographic parity difference: {dp_diff:.3f}")
        if dp_diff < 0.1:
            print("✓ Demographic parity satisfied (difference < 0.1)")
        else:
            print("✗ Demographic parity violated (difference >= 0.1)")
        
        # Equalized Odds
        print(f"\\n2. EQUALIZED ODDS ANALYSIS")
        print("-" * 40)
        eq_metrics, tpr_diff, fpr_diff = self.equalized_odds(sensitive_attribute)
        
        for group, metrics in eq_metrics.items():
            print(f"{sensitive_attribute}={group}: TPR={metrics['TPR']:.3f}, FPR={metrics['FPR']:.3f}")
        
        print(f"TPR difference: {tpr_diff:.3f}")
        print(f"FPR difference: {fpr_diff:.3f}")
        
        if tpr_diff < 0.1 and fpr_diff < 0.1:
            print("✓ Equalized odds satisfied (differences < 0.1)")
        else:
            print("✗ Equalized odds violated (differences >= 0.1)")
        
        # Individual Fairness
        print(f"\\n3. INDIVIDUAL FAIRNESS ANALYSIS")
        print("-" * 40)
        consistency_scores = self.individual_fairness_analysis()
        avg_consistency = np.mean(consistency_scores)
        print(f"Average individual fairness score: {avg_consistency:.3f}")
        print(f"(Lower scores indicate better individual fairness)")
        
        # Group-specific performance
        print(f"\\n4. GROUP-SPECIFIC PERFORMANCE")
        print("-" * 40)
        groups = np.unique(self.sensitive_features[sensitive_attribute])
        
        for group in groups:
            group_mask = self.sensitive_features[sensitive_attribute] == group
            group_accuracy = accuracy_score(self.y_test[group_mask], self.predictions[group_mask])
            group_size = np.sum(group_mask)
            print(f"{sensitive_attribute}={group}: Accuracy={group_accuracy:.3f}, Sample size={group_size}")
        
        return {
            'demographic_parity': {'rates': positive_rates, 'difference': dp_diff},
            'equalized_odds': {'metrics': eq_metrics, 'tpr_diff': tpr_diff, 'fpr_diff': fpr_diff},
            'individual_fairness': {'avg_score': avg_consistency, 'scores': consistency_scores}
        }
    
    def visualize_bias(self, sensitive_attribute):
        """Create visualizations for bias analysis"""
        fig, axes = plt.subplots(2, 2, figsize=(15, 10))
        
        # 1. Prediction distribution by group
        groups = np.unique(self.sensitive_features[sensitive_attribute])
        prediction_data = []
        
        for group in groups:
            group_mask = self.sensitive_features[sensitive_attribute] == group
            group_predictions = self.predictions[group_mask]
            prediction_data.extend([(group, pred) for pred in group_predictions])
        
        pred_df = pd.DataFrame(prediction_data, columns=[sensitive_attribute, 'Prediction'])
        sns.countplot(data=pred_df, x=sensitive_attribute, hue='Prediction', ax=axes[0,0])
        axes[0,0].set_title('Prediction Distribution by Group')
        
        # 2. Accuracy by group
        group_accuracies = []
        for group in groups:
            group_mask = self.sensitive_features[sensitive_attribute] == group
            group_acc = accuracy_score(self.y_test[group_mask], self.predictions[group_mask])
            group_accuracies.append(group_acc)
        
        axes[0,1].bar(groups, group_accuracies)
        axes[0,1].set_title('Accuracy by Group')
        axes[0,1].set_ylabel('Accuracy')
        
        # 3. Confusion matrices by group
        for i, group in enumerate(groups):
            group_mask = self.sensitive_features[sensitive_attribute] == group
            cm = confusion_matrix(self.y_test[group_mask], self.predictions[group_mask])
            
            if i == 0:
                sns.heatmap(cm, annot=True, fmt='d', ax=axes[1,0])
                axes[1,0].set_title(f'Confusion Matrix - {sensitive_attribute}={group}')
            else:
                sns.heatmap(cm, annot=True, fmt='d', ax=axes[1,1])
                axes[1,1].set_title(f'Confusion Matrix - {sensitive_attribute}={group}')
        
        plt.tight_layout()
        plt.show()

# Example usage with synthetic data
def create_biased_dataset():
    """Create a synthetic dataset with bias for demonstration"""
    np.random.seed(42)
    n_samples = 1000
    
    # Create features
    X = np.random.randn(n_samples, 5)
    
    # Create sensitive attribute (gender: 0=female, 1=male)
    gender = np.random.binomial(1, 0.5, n_samples)
    
    # Create biased target variable
    # Males have higher chance of positive outcome due to bias
    y = np.zeros(n_samples)
    for i in range(n_samples):
        # Base probability from features
        base_prob = 1 / (1 + np.exp(-(X[i, 0] + X[i, 1] * 0.5)))
        
        # Add bias: males get 0.3 probability boost
        if gender[i] == 1:
            base_prob += 0.3
        
        y[i] = np.random.binomial(1, min(base_prob, 1.0))
    
    # Create DataFrame for sensitive features
    sensitive_df = pd.DataFrame({'gender': gender})
    
    return X, y, sensitive_df

# Run fairness analysis
print("Creating biased dataset...")
X, y, sensitive_features = create_biased_dataset()

print("Initializing fairness analyzer...")
analyzer = FairnessAnalyzer()

print("Loading data...")
X_train, y_train = analyzer.load_data(X, y, sensitive_features)

print("Training model...")
analyzer.train_model(X_train, y_train, model_type='random_forest')

print("Generating fairness report...")
fairness_results = analyzer.generate_fairness_report('gender')

print("\\nCreating bias visualizations...")
analyzer.visualize_bias('gender')

print("\\nFairness analysis completed!")
`,
        language: 'python',
        category: 'tutorial'
      }
    ],
    quiz: {
      questions: [
        {
          id: 'ethics-principle',
          question: 'Which of the following is NOT one of the core ethical principles in AI?',
          options: [
            'Fairness and Non-Discrimination',
            'Transparency and Explainability',
            'Profit Maximization',
            'Privacy and Data Protection'
          ],
          correctAnswer: 2,
          explanation: 'Profit maximization is a business objective, not an ethical principle. The core ethical principles focus on fairness, transparency, privacy, accountability, and human autonomy.'
        }
      ]
    }
  },
  {
    id: 'ai-explainability',
    title: 'AI Explainability and Interpretability',
    description: 'Making AI decisions transparent and understandable to humans.',
    duration: '3.5 hours',
    difficulty: 'intermediate',
    content: `
# AI Explainability and Interpretability

## Understanding the Terminology

### Interpretability
**Definition**: The degree to which humans can understand the cause of a decision made by an AI system.

**Characteristics**:
- **Intrinsic**: Built into the model architecture
- **Model-agnostic**: Can be applied to any model type
- **Global**: Explains overall model behavior
- **Local**: Explains individual predictions

### Explainability
**Definition**: The ability to provide clear, understandable explanations for AI system decisions.

**Types of Explanations**:
- **Causal explanations**: Why did the model make this decision?
- **Counterfactual explanations**: What would need to change for a different outcome?
- **Contrastive explanations**: Why this outcome instead of another?
- **Example-based explanations**: Similar cases with known outcomes

## Why Explainability Matters

### 1. Trust and Acceptance
- **User confidence**: People trust systems they understand
- **Adoption barriers**: Black box systems face resistance
- **Error detection**: Explanations help identify model mistakes
- **Calibrated trust**: Appropriate level of reliance on AI

### 2. Legal and Regulatory Requirements
- **Right to explanation**: GDPR and similar regulations
- **Algorithmic accountability**: Government transparency requirements
- **Due process**: Fair treatment in legal proceedings
- **Audit requirements**: Compliance verification

### 3. Debugging and Improvement
- **Model validation**: Verify correct learning
- **Bias detection**: Identify unfair decision patterns
- **Performance analysis**: Understand failure modes
- **Feature engineering**: Guide data collection efforts

### 4. Domain Expertise Integration
- **Expert validation**: Domain experts can verify reasoning
- **Knowledge transfer**: Learn from AI insights
- **Hybrid decisions**: Combine AI and human judgment
- **Safety-critical applications**: Healthcare, autonomous vehicles

## Levels of Interpretability

### 1. Global Interpretability
Understanding the entire model behavior across all inputs.

**Methods**:
- **Feature importance**: Which features matter most overall?
- **Partial dependence plots**: How does each feature affect predictions?
- **Model visualization**: Visual representation of decision logic
- **Rule extraction**: Convert complex models to interpretable rules

### 2. Local Interpretability
Understanding individual predictions or specific regions.

**Methods**:
- **Instance-based explanations**: Why this specific prediction?
- **Local surrogate models**: Simple models for local regions
- **Attribution methods**: Which features contributed to this decision?
- **Counterfactual examples**: Minimal changes for different outcomes

### 3. Algorithmic Transparency
Understanding the learning algorithm and its properties.

**Aspects**:
- **Model architecture**: How is the model structured?
- **Training process**: How did the model learn?
- **Hyperparameters**: What settings were used?
- **Convergence behavior**: How stable is the learning?

## Explainability Methods

### 1. Model-Agnostic Methods

#### LIME (Local Interpretable Model-agnostic Explanations)
Explains individual predictions by approximating the model locally.

**Process**:
1. Perturb the input instance
2. Get predictions for perturbed instances
3. Train simple model on perturbed data
4. Use simple model to explain original prediction

**Advantages**: Works with any model, intuitive explanations
**Limitations**: Instability, locality assumptions

#### SHAP (SHapley Additive exPlanations)
Uses game theory to assign importance values to features.

**Key Properties**:
- **Efficiency**: Feature contributions sum to prediction difference
- **Symmetry**: Equal contribution for equally important features
- **Dummy**: Irrelevant features get zero contribution
- **Additivity**: Consistent across model combinations

**Variants**:
- **TreeSHAP**: Optimized for tree-based models
- **DeepSHAP**: For neural networks
- **KernelSHAP**: Model-agnostic approximation

#### Counterfactual Explanations
Show minimal changes needed for different outcomes.

**Example**: "If your income were $5,000 higher, you would have been approved for the loan."

**Benefits**: Actionable insights, intuitive understanding
**Challenges**: Realistic constraints, multiple valid counterfactuals

### 2. Model-Specific Methods

#### Linear Models
Inherently interpretable through coefficients.

**Interpretation**: Each coefficient shows feature impact on outcome
**Extensions**: Regularization for feature selection, interaction terms

#### Decision Trees
Transparent decision paths from root to leaf.

**Advantages**: Human-readable rules, handles interactions naturally
**Limitations**: Can become complex, instability

#### Attention Mechanisms
Show which parts of input the model focuses on.

**Applications**: 
- **NLP**: Which words are important for classification?
- **Computer Vision**: Which image regions drive decisions?
- **Time Series**: Which time steps matter most?

### 3. Post-hoc Explanation Methods

#### Gradient-based Methods
Use gradients to identify important input features.

**Techniques**:
- **Saliency maps**: Gradient magnitude indicates importance
- **Integrated gradients**: Accumulate gradients along path
- **GradCAM**: Gradient-weighted class activation mapping

#### Perturbation-based Methods
Measure importance by observing prediction changes.

**Approaches**:
- **Occlusion**: Remove features and observe impact
- **Permutation importance**: Shuffle features randomly
- **RISE**: Random input sampling for explanations

## Evaluation of Explanations

### 1. Fidelity
How well does the explanation reflect the actual model behavior?

**Metrics**:
- **Local fidelity**: Accuracy in local neighborhood
- **Global fidelity**: Overall agreement with model
- **Consistency**: Similar inputs produce similar explanations

### 2. Comprehensibility
How well can humans understand the explanations?

**Factors**:
- **Complexity**: Number of features or rules
- **Cognitive load**: Mental effort required
- **Domain alignment**: Matches human reasoning patterns
- **Visualization quality**: Clear, intuitive presentation

### 3. Completeness
Does the explanation capture all relevant factors?

**Considerations**:
- **Feature coverage**: All important features included
- **Interaction effects**: Complex relationships explained
- **Context dependency**: Situational factors considered

### 4. Actionability
Can humans use explanations to take meaningful action?

**Requirements**:
- **Modifiable features**: Factors users can actually change
- **Realistic constraints**: Feasible modifications
- **Cost considerations**: Effort required for changes
- **Legal/ethical bounds**: Permissible actions

## Challenges and Limitations

### 1. Explanation-Prediction Trade-off
More interpretable models may be less accurate.

**Dilemma**: Choose between performance and transparency
**Solutions**: 
- Model compression and distillation
- Hybrid architectures
- Progressive disclosure of complexity

### 2. Explanation Instability
Small input changes can lead to very different explanations.

**Causes**:
- Model sensitivity
- Explanation method limitations
- High-dimensional spaces

**Mitigation**:
- Ensemble explanations
- Stability regularization
- Robust explanation methods

### 3. Human Factors
People may misinterpret or over-rely on explanations.

**Issues**:
- **Confirmation bias**: Seeking explanations that confirm beliefs
- **Oversimplification**: Complex relationships reduced to simple rules
- **False confidence**: Explanations create illusion of understanding

### 4. Adversarial Explanations
Malicious actors can manipulate explanations while keeping predictions constant.

**Risks**:
- Misleading users about model behavior
- Hiding biased or unethical decision-making
- Regulatory circumvention

## Best Practices

### 1. Multi-Method Approach
Use multiple explanation techniques to build comprehensive understanding.

### 2. Stakeholder-Specific Explanations
Tailor explanations to different user needs and expertise levels.

### 3. Continuous Validation
Regularly check that explanations remain accurate as models evolve.

### 4. Documentation and Governance
Maintain clear records of explanation methods and their limitations.

### 5. User Testing
Evaluate explanation effectiveness with actual users in realistic scenarios.
`,
    objectives: [
      'Understand different types of AI explainability',
      'Implement popular explanation methods (LIME, SHAP)',
      'Evaluate the quality of explanations',
      'Choose appropriate explanation techniques for different use cases'
    ],
    codeExamples: [
      {
        id: 'explainability-implementation',
        title: 'AI Explainability Methods Implementation',
        description: 'Comprehensive implementation of LIME, SHAP, and other explainability techniques',
        code: `
import numpy as np
import pandas as pd
import matplotlib.pyplot as plt
import seaborn as sns
from sklearn.model_selection import train_test_split
from sklearn.ensemble import RandomForestClassifier
from sklearn.datasets import make_classification
from sklearn.metrics import accuracy_score, classification_report
import shap
import lime
import lime.lime_tabular
from sklearn.inspection import permutation_importance
import warnings
warnings.filterwarnings('ignore')

class ExplainabilityAnalyzer:
    def __init__(self, model, X_train, X_test, y_train, y_test, feature_names=None):
        self.model = model
        self.X_train = X_train
        self.X_test = X_test
        self.y_train = y_train
        self.y_test = y_test
        self.feature_names = feature_names or [f'feature_{i}' for i in range(X_train.shape[1])]
        
        # Train the model
        self.model.fit(X_train, y_train)
        self.predictions = self.model.predict(X_test)
        
        print(f"Model trained. Test accuracy: {accuracy_score(y_test, self.predictions):.3f}")
    
    def global_feature_importance(self):
        """Calculate global feature importance using multiple methods"""
        results = {}
        
        # 1. Built-in feature importance (for tree-based models)
        if hasattr(self.model, 'feature_importances_'):
            results['built_in'] = self.model.feature_importances_
        
        # 2. Permutation importance
        perm_importance = permutation_importance(
            self.model, self.X_test, self.y_test, 
            n_repeats=10, random_state=42
        )
        results['permutation'] = perm_importance.importances_mean
        results['permutation_std'] = perm_importance.importances_std
        
        return results
    
    def shap_explanations(self, max_evals=100):
        """Generate SHAP explanations"""
        print("Generating SHAP explanations...")
        
        # Choose appropriate explainer based on model type
        if hasattr(self.model, 'tree_'):
            # Tree-based model
            explainer = shap.TreeExplainer(self.model)
            shap_values = explainer.shap_values(self.X_test)
        else:
            # Model-agnostic explainer
            explainer = shap.KernelExplainer(
                self.model.predict_proba, 
                shap.sample(self.X_train, 100)
            )
            shap_values = explainer.shap_values(self.X_test[:50], nsamples=max_evals)
        
        return explainer, shap_values
    
    def lime_explanations(self, instance_idx=0, num_features=5):
        """Generate LIME explanations for a specific instance"""
        print(f"Generating LIME explanation for instance {instance_idx}...")
        
        # Create LIME explainer
        explainer = lime.lime_tabular.LimeTabularExplainer(
            self.X_train,
            feature_names=self.feature_names,
            class_names=['Class 0', 'Class 1'],
            mode='classification'
        )
        
        # Explain instance
        instance = self.X_test[instance_idx]
        explanation = explainer.explain_instance(
            instance, 
            self.model.predict_proba,
            num_features=num_features
        )
        
        return explanation
    
    def counterfactual_explanations(self, instance_idx=0, target_class=1):
        """Generate simple counterfactual explanations"""
        print(f"Generating counterfactual explanation for instance {instance_idx}...")
        
        instance = self.X_test[instance_idx].copy()
        original_pred = self.model.predict_proba([instance])[0]
        
        # Simple gradient-based approach
        # Find minimal changes to flip prediction
        
        counterfactuals = []
        for feature_idx in range(len(instance)):
            # Try increasing and decreasing each feature
            for direction in [1, -1]:
                modified_instance = instance.copy()
                step_size = 0.1
                max_steps = 20
                
                for step in range(max_steps):
                    modified_instance[feature_idx] += direction * step_size
                    new_pred = self.model.predict_proba([modified_instance])[0]
                    
                    if np.argmax(new_pred) == target_class:
                        # Found counterfactual
                        change = modified_instance[feature_idx] - instance[feature_idx]
                        counterfactuals.append({
                            'feature_idx': feature_idx,
                            'feature_name': self.feature_names[feature_idx],
                            'original_value': instance[feature_idx],
                            'new_value': modified_instance[feature_idx],
                            'change': change,
                            'new_probability': new_pred[target_class]
                        })
                        break
        
        # Sort by magnitude of change
        counterfactuals.sort(key=lambda x: abs(x['change']))
        
        return counterfactuals
    
    def visualize_global_importance(self, importance_results):
        """Visualize global feature importance"""
        fig, axes = plt.subplots(1, 2, figsize=(15, 6))
        
        # Built-in importance
        if 'built_in' in importance_results:
            importance_df = pd.DataFrame({
                'feature': self.feature_names,
                'importance': importance_results['built_in']
            }).sort_values('importance', ascending=True)
            
            axes[0].barh(importance_df['feature'], importance_df['importance'])
            axes[0].set_title('Built-in Feature Importance')
            axes[0].set_xlabel('Importance')
        
        # Permutation importance with error bars
        if 'permutation' in importance_results:
            perm_df = pd.DataFrame({
                'feature': self.feature_names,
                'importance': importance_results['permutation'],
                'std': importance_results['permutation_std']
            }).sort_values('importance', ascending=True)
            
            axes[1].barh(perm_df['feature'], perm_df['importance'], 
                        xerr=perm_df['std'], capsize=3)
            axes[1].set_title('Permutation Feature Importance')
            axes[1].set_xlabel('Importance')
        
        plt.tight_layout()
        plt.show()
    
    def visualize_shap(self, explainer, shap_values, plot_type='summary'):
        """Visualize SHAP explanations"""
        print(f"Creating SHAP {plot_type} plot...")
        
        if plot_type == 'summary':
            # Handle binary classification
            if isinstance(shap_values, list) and len(shap_values) == 2:
                shap_values_plot = shap_values[1]  # Use positive class
            else:
                shap_values_plot = shap_values
            
            shap.summary_plot(shap_values_plot, self.X_test, 
                            feature_names=self.feature_names, show=False)
            plt.show()
            
        elif plot_type == 'waterfall':
            if isinstance(shap_values, list):
                shap_values_plot = shap_values[1][0]  # First instance, positive class
            else:
                shap_values_plot = shap_values[0]
            
            shap.waterfall_plot(
                explainer.expected_value[1] if isinstance(explainer.expected_value, list) 
                else explainer.expected_value,
                shap_values_plot,
                self.X_test.iloc[0] if hasattr(self.X_test, 'iloc') else self.X_test[0],
                feature_names=self.feature_names,
                show=False
            )
            plt.show()
    
    def visualize_lime(self, lime_explanation):
        """Visualize LIME explanation"""
        # Show explanation in notebook
        lime_explanation.show_in_notebook(show_table=True)
        
        # Create bar plot
        explanation_list = lime_explanation.as_list()
        features, weights = zip(*explanation_list)
        
        plt.figure(figsize=(10, 6))
        colors = ['red' if w < 0 else 'blue' for w in weights]
        plt.barh(range(len(features)), weights, color=colors)
        plt.yticks(range(len(features)), features)
        plt.xlabel('LIME Weight')
        plt.title('LIME Feature Importance')
        plt.axvline(x=0, color='black', linestyle='-', alpha=0.3)
        plt.show()
    
    def visualize_counterfactuals(self, counterfactuals, instance_idx=0):
        """Visualize counterfactual explanations"""
        if not counterfactuals:
            print("No counterfactuals found")
            return
        
        print(f"Counterfactual Explanations for Instance {instance_idx}:")
        print("="*60)
        
        original_pred = self.model.predict_proba([self.X_test[instance_idx]])[0]
        print(f"Original prediction: Class {np.argmax(original_pred)} "
              f"(probability: {np.max(original_pred):.3f})")
        print()
        
        for i, cf in enumerate(counterfactuals[:5]):  # Show top 5
            print(f"Counterfactual {i+1}:")
            print(f"  Feature: {cf['feature_name']}")
            print(f"  Change: {cf['original_value']:.3f} → {cf['new_value']:.3f} "
                  f"(Δ {cf['change']:+.3f})")
            print(f"  New probability: {cf['new_probability']:.3f}")
            print()
    
    def comprehensive_analysis(self, instance_idx=0):
        """Run comprehensive explainability analysis"""
        print("="*60)
        print("COMPREHENSIVE EXPLAINABILITY ANALYSIS")
        print("="*60)
        
        # 1. Global Feature Importance
        print("\\n1. GLOBAL FEATURE IMPORTANCE")
        importance_results = self.global_feature_importance()
        self.visualize_global_importance(importance_results)
        
        # 2. SHAP Explanations
        print("\\n2. SHAP EXPLANATIONS")
        try:
            explainer, shap_values = self.shap_explanations()
            self.visualize_shap(explainer, shap_values, 'summary')
            self.visualize_shap(explainer, shap_values, 'waterfall')
        except Exception as e:
            print(f"SHAP analysis failed: {e}")
        
        # 3. LIME Explanations
        print("\\n3. LIME EXPLANATIONS")
        try:
            lime_explanation = self.lime_explanations(instance_idx)
            self.visualize_lime(lime_explanation)
        except Exception as e:
            print(f"LIME analysis failed: {e}")
        
        # 4. Counterfactual Explanations
        print("\\n4. COUNTERFACTUAL EXPLANATIONS")
        counterfactuals = self.counterfactual_explanations(instance_idx)
        self.visualize_counterfactuals(counterfactuals, instance_idx)
        
        print("\\nAnalysis completed!")

def create_sample_dataset():
    """Create a sample dataset for demonstration"""
    # Generate synthetic data
    X, y = make_classification(
        n_samples=1000,
        n_features=8,
        n_informative=6,
        n_redundant=2,
        n_clusters_per_class=1,
        random_state=42
    )
    
    # Create meaningful feature names
    feature_names = [
        'Income', 'Age', 'Education_Years', 'Credit_Score',
        'Debt_Ratio', 'Employment_Length', 'Savings', 'Expenses'
    ]
    
    # Create DataFrame
    df = pd.DataFrame(X, columns=feature_names)
    
    return df, y, feature_names

# Example usage
def run_explainability_demo():
    """Run complete explainability demonstration"""
    print("Creating sample dataset...")
    X, y, feature_names = create_sample_dataset()
    
    # Split data
    X_train, X_test, y_train, y_test = train_test_split(
        X, y, test_size=0.2, random_state=42, stratify=y
    )
    
    # Create model
    model = RandomForestClassifier(n_estimators=100, random_state=42)
    
    # Initialize analyzer
    analyzer = ExplainabilityAnalyzer(
        model, X_train, X_test, y_train, y_test, feature_names
    )
    
    # Run comprehensive analysis
    analyzer.comprehensive_analysis(instance_idx=0)

if __name__ == "__main__":
    run_explainability_demo()
`,
        language: 'python',
        category: 'tutorial'
      }
    ],
    practice: {
      task: 'Implement a custom explainability method that combines multiple explanation techniques to provide a comprehensive understanding of model decisions.',
      hints: [
        'Combine global and local explanation methods',
        'Weight different explanations based on reliability',
        'Create intuitive visualizations for non-technical users'
      ]
    }
  },
  {
    id: 'ai-safety-alignment',
    title: 'AI Safety and Alignment',
    description: 'Ensuring AI systems behave safely and remain aligned with human values.',
    duration: '3 hours',
    difficulty: 'advanced',
    content: `
# AI Safety and Alignment

## Introduction to AI Safety

AI Safety is the field focused on ensuring that AI systems operate safely and beneficially, especially as they become more capable and autonomous.

### Key Safety Challenges

#### 1. Specification Problems
**Issue**: Difficulty in precisely specifying what we want AI systems to do.

**Examples**:
- **Reward hacking**: AI finds unexpected ways to maximize reward
- **Goodhart's Law**: "When a measure becomes a target, it ceases to be a good measure"
- **Edge case failures**: AI behaves unexpectedly in novel situations

#### 2. Robustness Problems
**Issue**: AI systems fail when encountering conditions different from training.

**Types**:
- **Distributional shift**: Performance degrades on new data distributions
- **Adversarial attacks**: Malicious inputs cause failures
- **Corner cases**: Rare situations not covered in training

#### 3. Assurance Problems
**Issue**: Difficulty in verifying that AI systems will behave safely.

**Challenges**:
- **Testing limitations**: Cannot test all possible scenarios
- **Emergent behaviors**: Complex systems may exhibit unexpected properties
- **Black box opacity**: Difficulty understanding internal decision processes

## AI Alignment

AI Alignment focuses on ensuring AI systems pursue intended goals and remain beneficial as they become more capable.

### The Alignment Problem

As AI systems become more powerful, it becomes crucial that they:
1. **Understand** human intentions and values
2. **Adopt** these values as their own objectives
3. **Retain** alignment even as they learn and adapt

### Value Learning

**Challenge**: How can AI systems learn human values from human behavior?

#### Approaches:

**1. Preference Learning**
- Learn from human preferences and comparisons
- Inverse reinforcement learning
- Preference elicitation through queries

**2. Imitation Learning**
- Learn by observing human behavior
- Behavior cloning from expert demonstrations
- Assume humans act rationally toward their values

**3. Cooperative Inverse Reinforcement Learning**
- Model humans as trying to communicate their values
- Account for human irrationality and mistakes
- Interactive preference learning

### Reward Modeling

**Concept**: Train models to predict human judgments of AI behavior.

#### Process:
1. Collect human evaluations of AI actions
2. Train reward model to predict these evaluations
3. Use reward model to train AI system
4. Iterate with more human feedback

#### Challenges:
- **Human judgment inconsistency**: People disagree or change opinions
- **Evaluation difficulty**: Some outcomes hard for humans to assess
- **Gaming the reward model**: AI might exploit flaws in human evaluations

## Technical Safety Approaches

### 1. Robustness and Verification

#### Formal Verification
Mathematical proofs that systems meet specifications.

**Techniques**:
- **Model checking**: Exhaustively verify finite-state systems
- **Theorem proving**: Mathematical proofs of properties
- **Abstract interpretation**: Overapproximate system behavior

**Limitations**:
- Scalability to complex systems
- Difficulty specifying properties formally
- Gap between models and implementations

#### Adversarial Training
Train systems to be robust against worst-case inputs.

**Methods**:
- **Adversarial examples**: Train on maliciously crafted inputs
- **Domain randomization**: Train on diverse conditions
- **Robust optimization**: Optimize for worst-case performance

### 2. Interpretability and Monitoring

#### Mechanistic Interpretability
Understanding the internal workings of AI systems.

**Goals**:
- **Circuit analysis**: Identify specific computational mechanisms
- **Feature visualization**: Understand what models learn
- **Causal interventions**: Test understanding by modifying internals

#### Runtime Monitoring
Continuously monitor AI systems during operation.

**Approaches**:
- **Anomaly detection**: Identify unusual behaviors
- **Uncertainty quantification**: Know when AI is uncertain
- **Performance monitoring**: Track degradation over time

### 3. Safe Exploration

Ensuring AI systems learn safely without causing harm during training.

#### Methods:
- **Risk-constrained RL**: Add safety constraints to learning
- **Safe policy improvement**: Guarantee improvement with bounded risk
- **Simulation-based learning**: Learn in safe simulated environments
- **Human oversight**: Include human judgment in learning loop

### 4. Corrigibility and Control

Ensuring humans can maintain control over AI systems.

#### Concepts:
- **Corrigibility**: AI systems should allow themselves to be modified
- **Off switches**: Ability to shut down AI systems when needed
- **Value learning**: AI should want to learn and follow human values
- **Mild optimization**: Avoid extreme optimization that's hard to control

## Specific Safety Techniques

### 1. Constitutional AI
Train AI systems to follow a set of principles or "constitution."

#### Process:
1. Define constitutional principles
2. Train AI to critique its own outputs
3. Revise outputs to better follow principles
4. Reinforce learning from self-critiques

### 2. AI Feedback and Debate
Use AI systems to help evaluate other AI systems.

#### Debate Protocol:
1. Two AI systems argue for different positions
2. Human judge evaluates arguments
3. Train systems to make convincing arguments for correct positions

### 3. Iterated Distillation and Amplification (IDA)
Scale up human supervision by breaking down tasks.

#### Process:
1. Human demonstrates solving simple version of task
2. Train AI to imitate human on simple tasks
3. Use AI to help human solve more complex tasks
4. Repeat to scale to arbitrarily complex tasks

### 4. Cooperative AI
Design AI systems that cooperate effectively with humans and other AIs.

#### Considerations:
- **Multi-agent dynamics**: How do multiple AI systems interact?
- **Game theory**: What are equilibrium outcomes?
- **Mechanism design**: How to structure incentives for cooperation?

## Advanced Safety Concepts

### 1. Mesa-Optimization
When AI systems develop internal optimization processes.

**Risks**:
- **Inner alignment**: Internal optimizer may have different objectives
- **Deceptive alignment**: System appears aligned during training but not deployment
- **Capability generalization**: Internal optimizer becomes more capable

### 2. Distributional Shift and Dataset Shift
Performance degradation when deployment differs from training.

**Types**:
- **Covariate shift**: Input distribution changes
- **Prior probability shift**: Class frequencies change
- **Concept drift**: Underlying relationships change

**Mitigation**:
- **Domain adaptation**: Adapt models to new domains
- **Continual learning**: Update models with new data
- **Uncertainty quantification**: Detect when confident predictions aren't warranted

### 3. Scalable Oversight
Maintaining human oversight as AI capabilities grow.

**Challenges**:
- **Superhuman performance**: How to evaluate AI better than humans?
- **Complex domains**: Human evaluation becomes infeasible
- **Speed of operation**: AI systems operate faster than human oversight

**Approaches**:
- **AI-assisted evaluation**: Use AI to help humans evaluate AI
- **Decomposition**: Break complex tasks into human-evaluable pieces
- **Red teaming**: Adversarial testing of AI systems

## Governance and Policy

### 1. AI Governance Frameworks
Systems for overseeing AI development and deployment.

**Components**:
- **Standards and certification**: Technical standards for AI safety
- **Regulatory oversight**: Government regulation of AI systems
- **Industry self-regulation**: Voluntary standards and practices
- **International cooperation**: Global coordination on AI safety

### 2. Risk Assessment and Management
Systematic evaluation of AI risks.

**Process**:
1. **Risk identification**: What could go wrong?
2. **Risk assessment**: How likely and severe are risks?
3. **Risk mitigation**: How to reduce risks?
4. **Risk monitoring**: Ongoing evaluation of risk levels

### 3. Responsible Disclosure
Coordinated sharing of AI safety research and vulnerabilities.

**Principles**:
- **Transparency**: Share safety-relevant information
- **Responsible timing**: Consider misuse potential
- **Stakeholder engagement**: Include affected parties
- **Continuous improvement**: Learn from experience

## Future Challenges

### 1. Artificial General Intelligence (AGI)
AI systems with human-level or superhuman performance across domains.

**Unique challenges**:
- **Rapid capability gain**: Fast transition from narrow to general AI
- **Value alignment**: Ensuring AGI pursues human-compatible goals
- **Control problem**: Maintaining human agency in AGI-dominated world

### 2. AI Systems Designing AI Systems
Recursive self-improvement and AI-designed AI.

**Considerations**:
- **Intelligence explosion**: Rapid, uncontrolled capability growth
- **Alignment preservation**: Maintaining alignment through iterations
- **Human in the loop**: Ensuring human oversight of AI design process

### 3. Collective Intelligence
Multiple AI systems working together.

**Challenges**:
- **Emergent behaviors**: Properties arising from system interactions
- **Coordination failures**: Inability to achieve efficient outcomes
- **Power concentration**: Risk of AI systems accumulating too much influence
`,
    objectives: [
      'Understand core AI safety challenges and risks',
      'Learn technical approaches to AI alignment',
      'Implement safety techniques in AI systems',
      'Recognize governance and policy considerations'
    ],
    codeExamples: [
      {
        id: 'safety-techniques',
        title: 'AI Safety Techniques Implementation',
        description: 'Implementation of key AI safety techniques including robustness testing and safe exploration',
        code: `
import numpy as np
import torch
import torch.nn as nn
import torch.optim as optim
import matplotlib.pyplot as plt
from sklearn.model_selection import train_test_split
from sklearn.datasets import make_classification
import warnings
warnings.filterwarnings('ignore')

class SafetyMonitor:
    def __init__(self, model, threshold=0.5):
        self.model = model
        self.threshold = threshold
        self.anomaly_scores = []
        self.uncertainty_scores = []
        self.safety_violations = []
    
    def detect_anomalies(self, inputs, method='reconstruction_error'):
        """Detect anomalous inputs that might cause unsafe behavior"""
        anomaly_scores = []
        
        if method == 'reconstruction_error':
            # Simple autoencoder-based anomaly detection
            with torch.no_grad():
                if hasattr(self.model, 'encode') and hasattr(self.model, 'decode'):
                    encoded = self.model.encode(inputs)
                    reconstructed = self.model.decode(encoded)
                    errors = torch.mean((inputs - reconstructed) ** 2, dim=1)
                    anomaly_scores = errors.numpy()
                else:
                    # Fallback: use prediction confidence as anomaly score
                    predictions = torch.softmax(self.model(inputs), dim=1)
                    confidence = torch.max(predictions, dim=1)[0]
                    anomaly_scores = (1 - confidence).numpy()
        
        elif method == 'gradient_norm':
            # Use gradient norm as anomaly indicator
            inputs.requires_grad_(True)
            outputs = self.model(inputs)
            loss = torch.mean(outputs)
            gradients = torch.autograd.grad(loss, inputs, create_graph=True)[0]
            anomaly_scores = torch.norm(gradients, dim=1).detach().numpy()
        
        self.anomaly_scores.extend(anomaly_scores)
        
        # Flag anomalies above threshold
        anomalies = anomaly_scores > self.threshold
        return anomalies, anomaly_scores
    
    def estimate_uncertainty(self, inputs, method='monte_carlo_dropout'):
        """Estimate prediction uncertainty"""
        uncertainties = []
        
        if method == 'monte_carlo_dropout':
            # Enable dropout during inference for uncertainty estimation
            self.model.train()  # Enable dropout
            
            n_samples = 20
            predictions = []
            
            with torch.no_grad():
                for _ in range(n_samples):
                    pred = torch.softmax(self.model(inputs), dim=1)
                    predictions.append(pred)
            
            predictions = torch.stack(predictions)
            
            # Calculate entropy as uncertainty measure
            mean_pred = torch.mean(predictions, dim=0)
            uncertainties = -torch.sum(mean_pred * torch.log(mean_pred + 1e-8), dim=1)
            
            self.model.eval()  # Disable dropout
        
        elif method == 'prediction_variance':
            # Use prediction variance as uncertainty
            self.model.train()
            n_samples = 10
            predictions = []
            
            with torch.no_grad():
                for _ in range(n_samples):
                    pred = torch.softmax(self.model(inputs), dim=1)
                    predictions.append(pred)
            
            predictions = torch.stack(predictions)
            uncertainties = torch.var(predictions, dim=0).mean(dim=1)
            
            self.model.eval()
        
        self.uncertainty_scores.extend(uncertainties.numpy())
        return uncertainties.numpy()
    
    def check_safety_constraints(self, inputs, outputs, constraints):
        """Check if outputs violate safety constraints"""
        violations = []
        
        for constraint in constraints:
            if constraint['type'] == 'output_range':
                # Check if outputs are within allowed range
                min_val, max_val = constraint['range']
                violation = torch.any(outputs < min_val) or torch.any(outputs > max_val)
                violations.append({
                    'type': 'output_range',
                    'violated': violation.item(),
                    'constraint': constraint
                })
            
            elif constraint['type'] == 'fairness':
                # Simple fairness check (would need sensitive attributes in real implementation)
                # This is a placeholder for more complex fairness constraints
                violations.append({
                    'type': 'fairness',
                    'violated': False,  # Placeholder
                    'constraint': constraint
                })
        
        self.safety_violations.extend(violations)
        return violations

class AdversarialRobustness:
    def __init__(self, model):
        self.model = model
    
    def generate_adversarial_examples(self, inputs, targets, method='fgsm', epsilon=0.1):
        """Generate adversarial examples to test robustness"""
        inputs.requires_grad_(True)
        
        if method == 'fgsm':  # Fast Gradient Sign Method
            outputs = self.model(inputs)
            loss = nn.CrossEntropyLoss()(outputs, targets)
            
            # Calculate gradients
            gradients = torch.autograd.grad(loss, inputs, create_graph=False)[0]
            
            # Generate adversarial examples
            adversarial = inputs + epsilon * torch.sign(gradients)
            adversarial = torch.clamp(adversarial, 0, 1)  # Ensure valid range
            
        elif method == 'pgd':  # Projected Gradient Descent
            adversarial = inputs.clone()
            alpha = epsilon / 10  # Step size
            
            for _ in range(10):  # 10 iterations
                adversarial.requires_grad_(True)
                outputs = self.model(adversarial)
                loss = nn.CrossEntropyLoss()(outputs, targets)
                
                gradients = torch.autograd.grad(loss, adversarial, create_graph=False)[0]
                
                # Update adversarial example
                adversarial = adversarial + alpha * torch.sign(gradients)
                
                # Project back to epsilon ball
                perturbation = torch.clamp(adversarial - inputs, -epsilon, epsilon)
                adversarial = torch.clamp(inputs + perturbation, 0, 1)
                adversarial = adversarial.detach()
        
        return adversarial
    
    def test_robustness(self, inputs, targets, epsilons=[0.01, 0.05, 0.1, 0.2]):
        """Test model robustness across different perturbation strengths"""
        results = {}
        
        for epsilon in epsilons:
            adversarial = self.generate_adversarial_examples(inputs, targets, epsilon=epsilon)
            
            with torch.no_grad():
                adv_outputs = self.model(adversarial)
                adv_predictions = torch.argmax(adv_outputs, dim=1)
                
                # Calculate adversarial accuracy
                accuracy = torch.mean((adv_predictions == targets).float()).item()
                
                results[epsilon] = {
                    'accuracy': accuracy,
                    'examples': adversarial[:5]  # Store first 5 examples
                }
        
        return results

class SafeExploration:
    def __init__(self, model, safety_threshold=0.1):
        self.model = model
        self.safety_threshold = safety_threshold
        self.safe_states = []
        self.unsafe_states = []
    
    def is_safe_state(self, state, safety_function=None):
        """Determine if a state is safe for exploration"""
        if safety_function is None:
            # Default safety check: ensure state values are within reasonable bounds
            safety_score = torch.max(torch.abs(state))
            is_safe = safety_score < self.safety_threshold
        else:
            is_safe = safety_function(state)
        
        if is_safe:
            self.safe_states.append(state)
        else:
            self.unsafe_states.append(state)
        
        return is_safe
    
    def safe_action_selection(self, state, available_actions, risk_estimator=None):
        """Select actions that maintain safety"""
        safe_actions = []
        
        for action in available_actions:
            # Estimate next state (simplified simulation)
            next_state = self.simulate_transition(state, action)
            
            if risk_estimator:
                risk = risk_estimator(state, action, next_state)
                if risk < self.safety_threshold:
                    safe_actions.append(action)
            else:
                # Default: check if next state is safe
                if self.is_safe_state(next_state):
                    safe_actions.append(action)
        
        return safe_actions
    
    def simulate_transition(self, state, action):
        """Simulate state transition (placeholder)"""
        # This would be replaced with actual environment dynamics
        noise = torch.randn_like(state) * 0.1
        next_state = state + action + noise
        return next_state

class AlignmentMonitor:
    def __init__(self, model, human_values):
        self.model = model
        self.human_values = human_values
        self.alignment_scores = []
    
    def evaluate_alignment(self, actions, outcomes):
        """Evaluate how well actions align with human values"""
        alignment_score = 0
        
        for value in self.human_values:
            if value['type'] == 'fairness':
                # Measure fairness in outcomes
                score = self.measure_fairness(actions, outcomes)
                alignment_score += value['weight'] * score
            
            elif value['type'] == 'harm_minimization':
                # Measure harmful outcomes
                harm_score = self.measure_harm(outcomes)
                alignment_score += value['weight'] * (1 - harm_score)  # Higher score for less harm
            
            elif value['type'] == 'transparency':
                # Measure explainability of actions
                transparency_score = self.measure_transparency(actions)
                alignment_score += value['weight'] * transparency_score
        
        self.alignment_scores.append(alignment_score)
        return alignment_score
    
    def measure_fairness(self, actions, outcomes):
        """Measure fairness in outcomes (simplified)"""
        # Placeholder: check variance in outcomes
        outcome_variance = torch.var(outcomes)
        fairness_score = 1 / (1 + outcome_variance)  # Lower variance = higher fairness
        return fairness_score.item()
    
    def measure_harm(self, outcomes):
        """Measure potential harm in outcomes"""
        # Placeholder: negative outcomes indicate harm
        harm_score = torch.mean(torch.clamp(-outcomes, 0, float('inf')))
        return harm_score.item()
    
    def measure_transparency(self, actions):
        """Measure how explainable the actions are"""
        # Placeholder: consistent actions are more transparent
        action_consistency = 1 - torch.var(actions)
        return action_consistency.item()

class SafeAISystem:
    def __init__(self, model, safety_config):
        self.model = model
        self.safety_monitor = SafetyMonitor(model, safety_config.get('anomaly_threshold', 0.5))
        self.robustness_tester = AdversarialRobustness(model)
        self.safe_explorer = SafeExploration(model, safety_config.get('safety_threshold', 0.1))
        self.alignment_monitor = AlignmentMonitor(model, safety_config.get('human_values', []))
        
        self.safety_log = []
    
    def safe_predict(self, inputs, check_anomalies=True, estimate_uncertainty=True):
        """Make predictions with safety checks"""
        safety_info = {}
        
        # Check for anomalies
        if check_anomalies:
            anomalies, anomaly_scores = self.safety_monitor.detect_anomalies(inputs)
            safety_info['anomalies'] = anomalies
            safety_info['anomaly_scores'] = anomaly_scores
            
            if np.any(anomalies):
                print(f"Warning: {np.sum(anomalies)} anomalous inputs detected")
        
        # Estimate uncertainty
        if estimate_uncertainty:
            uncertainties = self.safety_monitor.estimate_uncertainty(inputs)
            safety_info['uncertainties'] = uncertainties
            
            high_uncertainty = uncertainties > 0.5  # Threshold for high uncertainty
            if np.any(high_uncertainty):
                print(f"Warning: {np.sum(high_uncertainty)} high-uncertainty predictions")
        
        # Make predictions
        with torch.no_grad():
            outputs = self.model(inputs)
            predictions = torch.softmax(outputs, dim=1)
        
        # Check safety constraints
        constraints = [
            {'type': 'output_range', 'range': (0, 1)},
            {'type': 'fairness', 'protected_attributes': []}
        ]
        violations = self.safety_monitor.check_safety_constraints(inputs, predictions, constraints)
        safety_info['constraint_violations'] = violations
        
        # Log safety information
        self.safety_log.append(safety_info)
        
        return predictions, safety_info
    
    def comprehensive_safety_test(self, test_inputs, test_targets):
        """Run comprehensive safety evaluation"""
        print("Running Comprehensive Safety Tests...")
        print("="*50)
        
        # 1. Robustness Testing
        print("\\n1. ADVERSARIAL ROBUSTNESS TEST")
        robustness_results = self.robustness_tester.test_robustness(test_inputs, test_targets)
        
        for epsilon, results in robustness_results.items():
            print(f"Epsilon {epsilon}: Adversarial Accuracy = {results['accuracy']:.3f}")
        
        # 2. Anomaly Detection
        print("\\n2. ANOMALY DETECTION TEST")
        # Create some anomalous inputs
        anomalous_inputs = test_inputs + torch.randn_like(test_inputs) * 0.5
        anomalies, scores = self.safety_monitor.detect_anomalies(anomalous_inputs)
        print(f"Detected {np.sum(anomalies)} anomalies out of {len(anomalous_inputs)} inputs")
        
        # 3. Uncertainty Estimation
        print("\\n3. UNCERTAINTY ESTIMATION TEST")
        uncertainties = self.safety_monitor.estimate_uncertainty(test_inputs)
        high_uncertainty_rate = np.mean(uncertainties > 0.5)
        print(f"High uncertainty rate: {high_uncertainty_rate:.3f}")
        
        # 4. Safe Exploration Test
        print("\\n4. SAFE EXPLORATION TEST")
        sample_state = test_inputs[0]
        available_actions = [torch.randn_like(sample_state) * 0.1 for _ in range(5)]
        safe_actions = self.safe_explorer.safe_action_selection(sample_state, available_actions)
        print(f"Safe actions: {len(safe_actions)} out of {len(available_actions)}")
        
        return {
            'robustness': robustness_results,
            'anomaly_detection': {'anomalies': anomalies, 'scores': scores},
            'uncertainty': uncertainties,
            'safe_exploration': len(safe_actions)
        }

# Example usage
def create_simple_model():
    """Create a simple neural network for demonstration"""
    return nn.Sequential(
        nn.Linear(10, 64),
        nn.ReLU(),
        nn.Dropout(0.2),
        nn.Linear(64, 32),
        nn.ReLU(),
        nn.Dropout(0.2),
        nn.Linear(32, 2)
    )

def run_safety_demo():
    """Run AI safety demonstration"""
    print("AI Safety Techniques Demonstration")
    print("="*40)
    
    # Create sample data
    X, y = make_classification(n_samples=1000, n_features=10, n_classes=2, random_state=42)
    X_train, X_test, y_train, y_test = train_test_split(X, y, test_size=0.2, random_state=42)
    
    # Convert to tensors
    X_train_tensor = torch.FloatTensor(X_train)
    X_test_tensor = torch.FloatTensor(X_test)
    y_train_tensor = torch.LongTensor(y_train)
    y_test_tensor = torch.LongTensor(y_test)
    
    # Create and train model
    model = create_simple_model()
    criterion = nn.CrossEntropyLoss()
    optimizer = optim.Adam(model.parameters(), lr=0.001)
    
    # Train model
    print("Training model...")
    model.train()
    for epoch in range(50):
        optimizer.zero_grad()
        outputs = model(X_train_tensor)
        loss = criterion(outputs, y_train_tensor)
        loss.backward()
        optimizer.step()
    
    model.eval()
    
    # Test basic accuracy
    with torch.no_grad():
        test_outputs = model(X_test_tensor)
        test_predictions = torch.argmax(test_outputs, dim=1)
        accuracy = torch.mean((test_predictions == y_test_tensor).float())
        print(f"Test accuracy: {accuracy:.3f}")
    
    # Configure safety system
    safety_config = {
        'anomaly_threshold': 0.5,
        'safety_threshold': 0.1,
        'human_values': [
            {'type': 'fairness', 'weight': 0.4},
            {'type': 'harm_minimization', 'weight': 0.4},
            {'type': 'transparency', 'weight': 0.2}
        ]
    }
    
    # Create safe AI system
    safe_system = SafeAISystem(model, safety_config)
    
    # Run safety tests
    safety_results = safe_system.comprehensive_safety_test(
        X_test_tensor[:50], y_test_tensor[:50]
    )
    
    # Test safe prediction
    print("\\n5. SAFE PREDICTION TEST")
    predictions, safety_info = safe_system.safe_predict(X_test_tensor[:10])
    print(f"Made predictions with safety monitoring")
    print(f"Anomalies detected: {np.sum(safety_info['anomalies'])}")
    print(f"Average uncertainty: {np.mean(safety_info['uncertainties']):.3f}")
    
    print("\\nSafety demonstration completed!")

if __name__ == "__main__":
    run_safety_demo()
`,
        language: 'python',
        category: 'tutorial'
      }
    ],
    practice: {
      task: 'Implement a Constitutional AI system that can critique and revise its own outputs based on a set of ethical principles.',
      hints: [
        'Define a constitution with specific ethical rules',
        'Implement a critique function that evaluates outputs',
        'Create a revision mechanism that improves outputs'
      ]
    }
  }
];